import { useState, useEffect } from "react";
import { showAlert } from '@/components/AppDialog';
import { useNavigate } from "react-router-dom";
import AppShell, { ACCENT, ACCENT_DEEP } from "../components/AppShell";
import { API_BASE_URL } from '@/utils/env';

type NotifPrefs = { whatsapp: boolean; email: boolean; inApp: boolean };

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors"
      style={{ background: checked ? ACCENT : '#D1D5DB' }}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phone, setPhone] = useState('');
  const [whatsApp, setWhatsApp] = useState('');
  const [role, setRole] = useState('');
  const [language, setLanguage] = useState('en');
  const [notif, setNotif] = useState<NotifPrefs>({ whatsapp: false, email: true, inApp: true });
  const [twoFactor, setTwoFactor] = useState(false);
  const [lastLogin, setLastLogin] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) { navigate('/login'); return; }
        const user = JSON.parse(userStr);
        const tokensStr = localStorage.getItem('authTokens');
        const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
        const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
          headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) }
        });
        if (resp.ok) {
          const json = await resp.json();
          if (json.success && json.user) {
            const u = json.user;
            setFullName(`${u.firstName || ''} ${u.lastName || ''}`.trim());
            setEmail(u.email || '');
            setEmailVerified(u.emailVerified || false);
            setPhone(u.phone || '');
            setWhatsApp(u.whatsApp || '');
            setRole(u.role || '');
            setLanguage(u.language || 'en');
            setNotif({
              whatsapp: u.notificationPrefs?.whatsapp ?? false,
              email: u.notificationPrefs?.email ?? true,
              inApp: u.notificationPrefs?.inApp ?? true,
            });
            setTwoFactor(u.security?.twoFactor ?? false);
            setLastLogin(u.security?.lastLoginAt || '');
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setFetching(false);
      }
    })();
  }, [navigate]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) { navigate('/login'); return; }
      const user = JSON.parse(userStr);
      const tokensStr = localStorage.getItem('authTokens');
      const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
      const [firstName, ...rest] = fullName.trim().split(' ');
      const lastName = rest.join(' ');
      const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
        },
        body: JSON.stringify({
          firstName, lastName, phone, whatsApp, role, language,
          notificationPrefs: notif,
          updatedAt: new Date().toISOString()
        })
      });
      if (!resp.ok) throw new Error('Failed to save');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
      showAlert('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => { localStorage.removeItem('user'); navigate('/'); };

  if (fetching) return <AppShell title="Profile" crumb="Profile" onLogout={handleSignOut} loading />;

  return (
    <AppShell
      title="Profile"
      subtitle="Manage your personal information and account preferences."
      crumb="Profile"
      onLogout={handleSignOut}
    >
      <div className="pb-24">

          {/* Personal information */}
          <div className="bg-white rounded-xl border border-gray-200 mb-4">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="text-sm font-semibold text-[#111827]">Personal information</div>
              <div className="text-xs text-[#6B7280]">Your identity across Conncct</div>
            </div>
            <div className="px-6 py-6">
              {/* Three across on wide screens: the card now spans the full shell,
                  and a name field 1300px wide helps nobody. */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5">Full name</label>
                  <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm acc-ring focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5">Email</label>
                  <div className="relative">
                    <input type="email" value={email} disabled
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 pr-20" />
                    {emailVerified && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200">
                        Verified
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5">Phone number</label>
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+971 50 000 0000"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm acc-ring focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5">WhatsApp number</label>
                  <input type="tel" value={whatsApp} onChange={e => setWhatsApp(e.target.value)} placeholder="+971 50 000 0000"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm acc-ring focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5">Role / title</label>
                  <input type="text" value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. CEO, Founder"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm acc-ring focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5">Preferred language</label>
                  <select value={language} onChange={e => setLanguage(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white acc-ring focus:outline-none">
                    <option value="en">English</option>
                    <option value="ar">العربية</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-white rounded-xl border border-gray-200 mb-4">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="text-sm font-semibold text-[#111827]">Notifications</div>
              <div className="text-xs text-[#6B7280]">Choose how we reach you</div>
            </div>
            <div className="divide-y divide-gray-100">
              {([
                ['whatsapp', 'WhatsApp notifications', 'Receive updates on WhatsApp'],
                ['email', 'Email notifications', 'Receive updates via email'],
                ['inApp', 'In-app notifications', 'See alerts inside Conncct'],
              ] as [keyof NotifPrefs, string, string][]).map(([key, label, desc]) => (
                <div key={key} className="px-6 py-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-[#111827]">{label}</div>
                    <div className="text-xs text-[#6B7280]">{desc}</div>
                  </div>
                  <Toggle checked={notif[key]} onChange={v => setNotif(prev => ({ ...prev, [key]: v }))} />
                </div>
              ))}
            </div>
          </div>

          {/* Security */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="text-sm font-semibold text-[#111827]">Security</div>
              <div className="text-xs text-[#6B7280]">Protect your account</div>
            </div>
            <div className="divide-y divide-gray-100">
              <div className="px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-[#111827]">Password</div>
                  <div className="text-xs text-[#6B7280]">Keep your account secure</div>
                </div>
                <button onClick={() => navigate('/forgot-password')}
                  className="btn-acc-soft px-3 py-1.5 text-xs font-medium border rounded-lg transition">
                  Change password
                </button>
              </div>
              <div className="px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-[#111827]">Two-factor authentication</div>
                  <div className="text-xs text-[#6B7280]">Add an extra layer of security</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${twoFactor ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500'}`}>
                    {twoFactor ? 'On' : 'Off'}
                  </span>
                  <Toggle checked={twoFactor} onChange={setTwoFactor} />
                </div>
              </div>
              {lastLogin && (
                <div className="px-6 py-4">
                  <div className="text-sm font-medium text-[#111827]">Last login</div>
                  <div className="text-xs text-[#6B7280] mt-0.5">
                    {new Date(lastLogin).toLocaleString('en-AE', { dateStyle: 'medium', timeStyle: 'short' })}
                  </div>
                </div>
              )}
            </div>
          </div>
      </div>

      {/* Fixed save bar */}
      <div className="fixed bottom-0 md:left-64 left-0 right-0 bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-3 z-20">
          {saved && (
            <span className="text-sm text-green-600 font-medium flex items-center gap-1">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Saved
            </span>
          )}
          <button onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium text-[#374151] border border-gray-300 rounded-lg hover:bg-gray-50 transition">
            Cancel
          </button>
          <button onClick={handleSave} disabled={loading}
            className="px-5 py-2 text-sm font-medium text-white rounded-lg transition disabled:opacity-50"
            style={{ background: ACCENT }}
            onMouseEnter={e => { e.currentTarget.style.background = ACCENT_DEEP; }}
            onMouseLeave={e => { e.currentTarget.style.background = ACCENT; }}>
            {loading ? 'Saving…' : 'Save'}
          </button>
      </div>
    </AppShell>
  );
}
